// Rótulos das telas "Pedidos ao dev". Funções (não constantes) para o idioma trocar em runtime;
// cada `__t('literal')` é visto pelo extrator do catálogo es.
import { __t } from '../../i18n/t';
import {
  DevRequest, DevRequestAction as A, DevRequestEventType as E, DevRequestLane, DevRequestProject, DevRequestStatus as S, DevRequestType,
} from '../../models/devRequest';
import { elapsed, isDraft, LaneState } from './actions';

export type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error';

export const statusLabel = (status: S): string => {
  switch (status) {
    case S.pending: return __t('Pendente');
    case S.inProgress: return __t('Em andamento');
    case S.waitingSupport: return __t('Aguardando suporte');
    case S.waitingApproval: return __t('Aguardando aprovação');
    case S.waitingMigration: return __t('Aguardando migração');
    case S.completed: return __t('Concluído');
    case S.delivered: return __t('Entregue');
    case S.resolved: return __t('Resolvido');
    case S.failed: return __t('Falhou');
    case S.canceled: return __t('Cancelado');
    default: return String(status);
  }
};

export const statusColor = (status: S): ChipColor => {
  switch (status) {
    case S.pending: return 'default';
    case S.inProgress: return 'info';
    case S.waitingSupport: return 'warning';
    case S.waitingApproval: return 'secondary';
    case S.waitingMigration: return 'secondary';
    case S.completed: return 'primary';
    case S.delivered: return 'success';
    case S.resolved: return 'success';
    case S.failed: return 'error';
    default: return 'default';
  }
};

/** Uma frase do que está acontecendo, para o suporte. */
export const statusHint = (status: S): string => {
  switch (status) {
    case S.pending: return __t('Na fila. O robô pega o pedido em breve.');
    case S.inProgress: return __t('O robô está trabalhando neste pedido.');
    case S.waitingSupport: return __t('O robô tem perguntas. Responda abaixo para ele continuar.');
    case S.waitingApproval: return __t('O pedido precisa da aprovação do dev antes de seguir.');
    case S.waitingMigration: return __t('O dev precisa rodar uma alteração no banco. Depois disso o pedido segue sozinho.');
    case S.completed: return __t('Enviado para produção. Falta o deploy terminar.');
    case S.delivered: return __t('Entregue. Confira e marque como resolvido, ou reabra com um comentário.');
    case S.resolved: return __t('Resolvido.');
    case S.failed: return __t('O robô não conseguiu concluir. O dev foi avisado.');
    case S.canceled: return __t('Pedido cancelado.');
    default: return '';
  }
};

/** Situação para a tela: rascunho é pendente que o robô ainda não vê. */
export const requestStatus = (r: DevRequest): { label: string; color: ChipColor; hint: string } =>
  (isDraft(r)
    ? { label: __t('Rascunho'), color: 'warning', hint: __t('Este pedido ainda não foi enviado. O robô só começa depois de Enviar ao robô.') }
    : { label: statusLabel(r.status), color: statusColor(r.status), hint: statusHint(r.status) });

const STATUS_ORDER = [
  S.pending, S.inProgress, S.waitingSupport, S.waitingApproval, S.waitingMigration,
  S.completed, S.delivered, S.resolved, S.failed, S.canceled,
];
export const statusChoices = () => STATUS_ORDER.map(id => ({ id, name: statusLabel(id) }));

export const typeLabel = (type: DevRequestType): string => {
  switch (type) {
    case DevRequestType.feature: return __t('Funcionalidade');
    case DevRequestType.bug: return __t('Bug');
    case DevRequestType.investigation: return __t('Investigação');
    case DevRequestType.task: return __t('Tarefa');
    default: return String(type);
  }
};
/** Dica embaixo do campo Tipo: o que cada tipo quer dizer. */
export const typeHint = (type: DevRequestType | undefined): string => {
  switch (type) {
    case DevRequestType.feature: return __t('Algo novo no sistema.');
    case DevRequestType.bug: return __t('Algo que deveria funcionar e não funciona.');
    case DevRequestType.investigation: return __t('Só descobrir o que aconteceu, sem mudar nada.');
    case DevRequestType.task: return __t('Outra demanda, como alterar dados no banco. Mudança de dado passa pela aprovação do desenvolvedor.');
    default: return '';
  }
};
export const typeChoices = () => Object.values(DevRequestType).map(id => ({ id, name: typeLabel(id) }));

/** Hora do relógio; outro dia leva a data junto. */
export const clockLabel = (iso: string | null | undefined, now: number): string => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) { return '—'; }
  const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d);
  const sameDay = new Date(now).toDateString() === d.toDateString();
  return sameDay ? time : `${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(d)} ${time}`;
};

export const laneCadence = (lane: DevRequestLane): string => {
  switch (lane) {
    case DevRequestLane.urgent: return __t('Verifica a cada 1 min');
    case DevRequestLane.resume: return __t('Respostas, aprovações e reaberturas a cada 5 min');
    case DevRequestLane.normal: return __t('Verifica de hora em hora, na hora cheia');
    default: return '';
  }
};

export const laneStateLabel = (state: LaneState): string => {
  switch (state) {
    case 'busy': return __t('Trabalhando');
    case 'silent': return __t('Parada');
    default: return __t('Ativa');
  }
};

export const laneLabel = (lane: DevRequestLane): string => {
  switch (lane) {
    case DevRequestLane.urgent: return __t('Urgente');
    case DevRequestLane.resume: return __t('Retomada');
    case DevRequestLane.normal: return __t('Normal');
    default: return String(lane);
  }
};

/** "há 40 s", "há 3 min", "há 2 h", "há 4 d". */
export const agoLabel = (iso: string | null | undefined, now: number): string => {
  const e = elapsed(iso, now);
  if (!e) { return __t('sem sinal'); }
  switch (e.unit) {
    case 's': return __t('há %{n} s', { n: e.n });
    case 'min': return __t('há %{n} min', { n: e.n });
    case 'h': return __t('há %{n} h', { n: e.n });
    default: return __t('há %{n} d', { n: e.n });
  }
};

export const eventLabel = (type: E): string => {
  switch (type) {
    case E.created: return __t('Pedido criado');
    case E.sent: return __t('Enviado ao robô');
    case E.edited: return __t('Pedido editado');
    case E.claimed: return __t('Robô pegou o pedido');
    case E.claudeRun: return __t('Execução do Claude');
    case E.question: return __t('Perguntas do robô');
    case E.answer: return __t('Respostas do suporte');
    case E.approvalRequested: return __t('Aprovação pedida');
    case E.approved: return __t('Aprovado');
    case E.rejected: return __t('Recusado');
    case E.mergedDevelop: return __t('Código no develop');
    case E.migrationPending: return __t('Migração pendente');
    case E.migrationConfirmed: return __t('Migração confirmada');
    case E.deployStarted: return __t('Deploy iniciado');
    case E.deployOk: return __t('Deploy concluído');
    case E.deployFailed: return __t('Deploy falhou');
    case E.completed: return __t('Concluído');
    case E.delivered: return __t('Entregue');
    case E.resolved: return __t('Resolvido');
    case E.reopened: return __t('Reaberto');
    case E.canceled: return __t('Cancelado');
    case E.failed: return __t('Falhou');
    case E.retry: return __t('Nova tentativa');
    case E.stale: return __t('Sem sinal, voltou para a fila');
    case E.error: return __t('Erro');
    case E.comment: return __t('Comentário');
    case E.note: return __t('Nota');
    case E.statusChanged: return __t('Status alterado');
    default: return String(type);
  }
};

export const actionDoneMessage = (action: A): string => {
  switch (action) {
    case A.send: return __t('Pedido enviado ao robô.');
    case A.answer: return __t('Respostas enviadas.');
    case A.approve: return __t('Pedido aprovado.');
    case A.reject: return __t('Pedido recusado.');
    case A.migrated: return __t('Migração confirmada. O pedido volta para a fila.');
    case A.resolve: return __t('Pedido resolvido.');
    case A.reopen: return __t('Pedido reaberto.');
    case A.retry: return __t('Pedido volta para a fila.');
    case A.cancel: return __t('Pedido cancelado.');
    case A.comment: return __t('Comentário registrado.');
    default: return '';
  }
};

export const deployStatusLabel = (status: DevRequestProject['deployStatus']): string => {
  switch (status) {
    case 'pending': return __t('Pendente');
    case 'merged_develop': return __t('No develop');
    case 'deploying': return __t('Em deploy');
    case 'success': return __t('No ar');
    case 'failed': return __t('Falhou');
    case 'manual': return __t('Deploy manual');
    case 'store': return __t('Loja de apps');
    default: return String(status);
  }
};

export const deployStatusColor = (status: DevRequestProject['deployStatus']): ChipColor => {
  switch (status) {
    case 'success': return 'success';
    case 'failed': return 'error';
    case 'deploying': return 'info';
    case 'manual': return 'warning';
    case 'store': return 'warning';
    default: return 'default';
  }
};

export const fmtUsd = (value?: number | null): string =>
  (value === null || value === undefined ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value)));

export const resumeCommand = (sessionId: string): string => `cd /Volumes/External/Repos/PontuaX && claude --resume ${sessionId}`;
